"use no memo";

import { useCallback, useEffect, useState, useTransition } from "react";

import { Autocomplete } from "@stacklet/ui";
import { Controller, useFormContext } from "react-hook-form";
import { graphql, useRefetchableFragment } from "react-relay";

import { useProvider } from "app/contexts/ProviderContext";
import {
  createRoleAssignmentChangeHandler,
  toAutocompleteValue,
  type RoleAssignmentComboItem,
} from "app/hooks/forms";
import {
  hasDeployPoliciesPermission,
  hasViewPermission,
} from "app/utils/rbac/permissions";

import type { AccountGroupPicker_accountGroups$key } from "./__generated__/AccountGroupPicker_accountGroups.graphql";
import type { AccountGroupPickerRefetchQuery } from "./__generated__/AccountGroupPickerRefetchQuery.graphql";

/** Props for the AccountGroupPicker component */
interface Props {
  /** GraphQL query reference for account groups data */
  queryRef: AccountGroupPicker_accountGroups$key;
  /** Whether to allow selecting multiple account groups */
  multiple?: boolean;
  /** Whether the picker should be disabled */
  disabled?: boolean;
  /** Name of the form field for react-hook-form */
  fieldName?: string;
  /** Whether to filter account groups by deploy policies permission */
  requireDeployPoliciesPermission?: boolean;
  /** Label for the picker */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
}

export function AccountGroupPicker({
  queryRef,
  multiple = false,
  disabled = false,
  fieldName = "accountGroup",
  requireDeployPoliciesPermission = false,
  label = "Account groups *",
  placeholder = "Account groups *",
}: Props) {
  const [, startTransition] = useTransition();
  const [searchValue, setSearchValue] = useState("");
  const { control } = useFormContext();
  const provider = useProvider();
  const [data, refetch] = useRefetchableFragment<
    AccountGroupPickerRefetchQuery,
    AccountGroupPicker_accountGroups$key
  >(
    graphql`
      fragment AccountGroupPicker_accountGroups on Query
      @argumentDefinitions(first: { type: "Int", defaultValue: 1000 })
      @refetchable(queryName: "AccountGroupPickerRefetchQuery") {
        accountGroups(
          filterElement: $accountGroupFilterElement
          first: $first
        ) {
          edges {
            node {
              name
              uuid
              roleAssignmentTarget
              userPermissions
            }
          }
        }
      }
    `,
    queryRef,
  );

  const filteredEdges = (data?.accountGroups?.edges ?? []).filter(
    (edge) =>
      hasViewPermission(edge.node.userPermissions) &&
      (requireDeployPoliciesPermission
        ? hasDeployPoliciesPermission(edge.node.userPermissions)
        : true),
  );

  const options: RoleAssignmentComboItem[] = filteredEdges.map((edge) => ({
    id: edge.node.uuid,
    name: edge.node.name,
    roleAssignmentTarget: edge.node.roleAssignmentTarget,
  }));

  const refetchAccountGroups = useCallback(
    (search: string) => {
      const value = search.trim();

      if (value) {
        refetch({
          first: 1000,
          accountGroupFilterElement: {
            multiple: {
              operator: "AND",
              operands: [
                {
                  single: {
                    name: "name",
                    value,
                  },
                },
                {
                  single: {
                    name: "provider",
                    value: provider,
                  },
                },
              ],
            },
          },
        });
      } else {
        refetch({
          first: 1000,
          accountGroupFilterElement: {
            single: {
              name: "provider",
              value: provider,
            },
          },
        });
      }
    },
    [provider, refetch],
  );

  useEffect(() => {
    startTransition(() => {
      refetchAccountGroups(searchValue);
    });
  }, [searchValue, refetchAccountGroups]);

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <Autocomplete
          data-testid="account-group-picker"
          inputRef={field.ref}
          isDisabled={disabled}
          items={options}
          label={label}
          menuTrigger="focus"
          multiple={multiple}
          onBlur={field.onBlur}
          onChange={createRoleAssignmentChangeHandler(field.onChange, multiple)}
          onInputChange={setSearchValue}
          placeholder={placeholder}
          tag={(item: RoleAssignmentComboItem) => (
            <Autocomplete.Tag id={item.id} textValue={item.name}>
              {item.name}
            </Autocomplete.Tag>
          )}
          value={toAutocompleteValue(field.value, multiple)}
        >
          {(item: RoleAssignmentComboItem) => (
            <Autocomplete.Option id={item.id} textValue={item.name}>
              <span>{item.name}</span>
            </Autocomplete.Option>
          )}
        </Autocomplete>
      )}
    />
  );
}
